export class Constants {
  static highestZIndex = 999999999;
  static beforeHighestZIndex = 999999998;

  static refetchIntervalForRewriteInBulkProgress = 30_000;

  static linkedinBaseUrl = 'https://www.linkedin.com';
  static salesNavBaseUrl = 'https://www.linkedin.com/sales';
  static cronoCustomPlanPage = 'https://www.crono.one/custom-plan';

  static webhookDocsLink =
    'https://help.crono.one/en/articles/73-webhooks-in-crono';
  static mcpDocsLink =
    'https://help.crono.one/en/articles/77-crono-mcp-integration';

  static notInSyncDefaultMessage = 'Not in sync';

  static base64AudioRecordingPrefix = 'data:audio/webm;codecs=opus;base64,';

  static debouncedSearchDelay = 800;

  static numberOfTags = 3;
  static numberOfTagsColor = 12;
  static defaultReportLimit = 100;
  static defaultLimit = 50;
  static defaultTemplatesLimit = 50;
  static defaultStrategiesLimit = 25;
  static defaultSignalsLimit = 20;
  static defaultCallIntegrationsLimit = 25;
  static freeSignalsLimit = 3;
  static defaultExportDataLimit = 10_000;

  static generateExportFileName(
    type: 'companies' | 'contacts' | 'leads',
    format: 'csv' | 'xlsx',
  ): string {
    const today = new Date().toISOString().split('T')[0];
    const typeLabels = {
      companies: 'companies',
      contacts: 'contacts',
      leads: 'leads',
    };
    return `${today} Crono ${typeLabels[type]}.${format}`;
  }

  static defaultLinkedinInvitationMessage = 'Linkedin Invitation sent.';

  static defaultImageUrl =
    'https://crono-assets.s3.eu-central-1.amazonaws.com/defaultCompanyLogo.png';
  static alexUrl = 'https://www.linkedin.com/in/alexroggero/';
  static websiteRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,24}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

  static allLanguage = 'all';
  static templateLanguagesInput = [
    'en', // English
    'es', // Spanish
    'fr', // French
    'de', // German
    'it', // Italian
    'pt', // Portuguese
    'nl', // Dutch
    'no', // Norwegian
    'sv', // Swedish
    'da', // Danish
    'fi', // Finnish
    'pl', // Polish
    'ro', // Romanian
    'bg', // Bulgarian
    'hu', // Hungarian
    'el', // Greek
    'lt', // Lithuanian
    'tr', // Turkish
  ] as const;

  static templateLanguages = [
    ...Constants.templateLanguagesInput,
    Constants.allLanguage,
  ] as const;

  static emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  static phoneRegex =
    /(?=(?:\D*\d){7,})((\+\d{1,2}\s?)?1?-?\.?\s?\(?\d{1,4}\)?[\s.-]?)?\d{1,4}[\s.-]?\d{1,4}[\s.-]?\d{1,4}/;

  static linkedinInvitationMaxCharsSalesNav = 300;

  static linkedinInvitationMaxChars = 200;

  static minAutomaticDailyDefault = 0;
  static maxAutomaticDailyDefault = 50;
  static maxAutomaticDailySalesNav = 100;

  static defaultAverageAutomatorDelay = 3;
  static minAverageAutomatorDelay = 1;
  static maxAverageAutomatorDelay = 5;

  static multiselectOptionsDivider = ';';

  static customSignatureOpenTag = '<section';

  static monthsDictionary: Record<number, string> = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December',
  };

  //#region Onboarding Links
  // Onboarding video URLs
  // Be careful since ReactPlayer doesn't support all formats of URLs (example watch?v= is not supported)
  // Next approach is recommended: Go on youtube video, click share button, copy from there, test in Crono if both preview and play is working
  static companyContactsSetupUrl =
    'https://youtu.be/3cZqRxOq9vY?si=Euc2PYDxzt-C4trr';
  static templateSetupUrl = 'https://youtu.be/CmC983I0cxk?si=H5LUbKSj4LbvZfhs';
  static stategySetupUrl = 'https://youtu.be/kmmlLbPt7bA?si=cYxNXxrjJ_CcFEkY';
  static taskExecutionUrl = 'https://youtu.be/f-1RC4HQ4jw?si=9nogV5vRAP2x5MpH';

  // Onboarding help links
  static helpLinkDefault = 'https://help.crono.one/en';
  static helpLinkIntegrations =
    'https://help.crono.one/en/articles/3-how-to-set-up-the-chrome-extension';
  static helpLinkContactsCompanies =
    'https://help.crono.one/en/articles/6-how-to-create-new-contacts-and-companies';
  static helpLinkStrategy =
    'https://help.crono.one/en/articles/16-how-to-create-a-sequence';
  static helpLinkTask =
    'https://help.crono.one/en/articles/25-how-to-create-and-execute-tasks';
  //#endregion

  static linkedinContactUrl = (publicId: string) =>
    `${Constants.linkedinBaseUrl}/in/${publicId}`;
  static linkedinCompanyUrl = (publicId: string) =>
    `${Constants.linkedinBaseUrl}/company/${publicId}`;
  static linkedinActivityRedirectUrl = (publicId: string) =>
    `${Constants.linkedinBaseUrl}/in/${publicId}/recent-activity/all`;
  static linkedinExperiencesRedirectUrl = (publicId: string) =>
    `${Constants.linkedinBaseUrl}/in/${publicId}/details/experience/`;
  static linkedinSalesNavLeadRedirectUrl = (leadId: string) =>
    `${Constants.linkedinBaseUrl}/sales/lead/${leadId},NAME_SEARCH`;
  static linkedinSalesNavCompanyRedirectUrl = (numericId: string) =>
    `${Constants.linkedinBaseUrl}/sales/company/${numericId}`;

  static urlRegex =
    /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;

  static PREFIX_POSSIBLE_JSESSIONID_ERROR = 'Error_JSESSIONID_';

  static defaultLanguage = 'en';

  static QUERY_PARAM_OPEN_POPUP = 'a';
  static QUERY_PARAM_OPEN_POPUP_VALUE = 'true';
}

export interface StatusColors {
  light: string;
  lightHover: string;
  dark: string;
}

export const statusColors = {
  red: {
    light: '#FFE2E2',
    lightHover: '#FDD0D0',
    dark: '#FD6778',
  },
  pink: {
    light: '#FDE5F8',
    lightHover: '#F3CEEB',
    dark: '#F376D8',
  },
  violet: {
    light: '#F2EAFF',
    lightHover: '#E4D8F6',
    dark: '#995AFF',
  },
  purple: {
    light: '#E4E4FF',
    lightHover: '#CDCDE5',
    dark: '#6E5DDD',
  },
  blue: {
    light: '#DEECFF',
    lightHover: '#C8D4E5',
    dark: '#3B58DB',
  },
  cyan: {
    light: '#D3EDFF',
    lightHover: '#BED5E5',
    dark: '#509AFC',
  },
  lightBlue: {
    light: '#D7F1FA',
    lightHover: '#C1D9E1',
    dark: '#2DC2F1',
  },
  acquamarine: {
    light: '#CEEDED',
    lightHover: '#B9D7CC',
    dark: '#1EBAB2',
  },
  green: {
    light: '#D5ECCF',
    lightHover: '#C0D4BA',
    dark: '#24BA84',
  },
  lime: {
    light: '#EDF7C8',
    lightHover: '#D5DEB4',
    dark: '#A8C052',
  },
  yellow: {
    light: '#FEF3D2',
    lightHover: '#F4E4C6',
    dark: '#F9BB06',
  },
  orange: {
    light: '#FFEBDD',
    lightHover: '#F3E0D3',
    dark: '#FD9467',
  },
};

export const statusColorsMap: {
  [key: number]: StatusColors[];
} = {
  1: [statusColors.red],
  2: [statusColors.red, statusColors.green],
  3: [statusColors.red, statusColors.green, statusColors.yellow],
  4: [
    statusColors.red,
    statusColors.cyan,
    statusColors.green,
    statusColors.yellow,
  ],
  5: [
    statusColors.red,
    statusColors.violet,
    statusColors.cyan,
    statusColors.green,
    statusColors.yellow,
  ],
  6: [
    statusColors.red,
    statusColors.pink,
    statusColors.violet,
    statusColors.cyan,
    statusColors.green,
    statusColors.yellow,
  ],
  7: [
    statusColors.red,
    statusColors.pink,
    statusColors.violet,
    statusColors.cyan,
    statusColors.green,
    statusColors.yellow,
    statusColors.orange,
  ],
  8: [
    statusColors.red,
    statusColors.pink,
    statusColors.violet,
    statusColors.blue,
    statusColors.cyan,
    statusColors.green,
    statusColors.yellow,
    statusColors.orange,
  ],
  9: [
    statusColors.red,
    statusColors.pink,
    statusColors.violet,
    statusColors.blue,
    statusColors.cyan,
    statusColors.acquamarine,
    statusColors.green,
    statusColors.yellow,
    statusColors.orange,
  ],
  10: [
    statusColors.red,
    statusColors.pink,
    statusColors.violet,
    statusColors.blue,
    statusColors.cyan,
    statusColors.acquamarine,
    statusColors.green,
    statusColors.lime,
    statusColors.yellow,
    statusColors.orange,
  ],
  11: [
    statusColors.red,
    statusColors.pink,
    statusColors.violet,
    statusColors.purple,
    statusColors.blue,
    statusColors.cyan,
    statusColors.acquamarine,
    statusColors.green,
    statusColors.lime,
    statusColors.yellow,
    statusColors.orange,
  ],
  12: [
    statusColors.red,
    statusColors.pink,
    statusColors.violet,
    statusColors.purple,
    statusColors.blue,
    statusColors.cyan,
    statusColors.lightBlue,
    statusColors.acquamarine,
    statusColors.green,
    statusColors.lime,
    statusColors.yellow,
    statusColors.orange,
  ],
};

export interface LinkedinSearchCategoryType {
  value: string;
  label: string;
}

export const COUNTRIES = [
  {
    title: 'Afghanistan',
    value: 'AF',
  },
  {
    title: 'Aland Islands',
    value: 'AX',
  },
  {
    title: 'Albania',
    value: 'AL',
  },
  {
    title: 'Algeria',
    value: 'DZ',
  },
  {
    title: 'American Samoa',
    value: 'AS',
  },
  {
    title: 'Andorra',
    value: 'AD',
  },
  {
    title: 'Angola',
    value: 'AO',
  },
  {
    title: 'Anguilla',
    value: 'AI',
  },
  {
    title: 'Antarctica',
    value: 'AQ',
  },
  {
    title: 'Antigua and Barbuda',
    value: 'AG',
  },
  {
    title: 'Argentina',
    value: 'AR',
  },
  {
    title: 'Armenia',
    value: 'AM',
  },
  {
    title: 'Aruba',
    value: 'AW',
  },
  {
    title: 'Australia',
    value: 'AU',
  },
  {
    title: 'Austria',
    value: 'AT',
  },
  {
    title: 'Azerbaijan',
    value: 'AZ',
  },
  {
    title: 'Bahamas',
    value: 'BS',
  },
  {
    title: 'Bahrain',
    value: 'BH',
  },
  {
    title: 'Bangladesh',
    value: 'BD',
  },
  {
    title: 'Barbados',
    value: 'BB',
  },
  {
    title: 'Belarus',
    value: 'BY',
  },
  {
    title: 'Belgium',
    value: 'BE',
  },
  {
    title: 'Belize',
    value: 'BZ',
  },
  {
    title: 'Benin',
    value: 'BJ',
  },
  {
    title: 'Bermuda',
    value: 'BM',
  },
  {
    title: 'Bhutan',
    value: 'BT',
  },
  {
    title: 'Bolivia',
    value: 'BO',
  },
  {
    title: 'Bosnia and Herzegovina',
    value: 'BA',
  },
  {
    title: 'Botswana',
    value: 'BW',
  },
  {
    title: 'Bouvet Island',
    value: 'BV',
  },
  {
    title: 'Brazil',
    value: 'BR',
  },
  {
    title: 'British Indian Ocean Territory',
    value: 'IO',
  },
  {
    title: 'British Virgin Islands',
    value: 'VG',
  },
  {
    title: 'Brunei',
    value: 'BN',
  },
  {
    title: 'Bulgaria',
    value: 'BG',
  },
  {
    title: 'Burkina Faso',
    value: 'BF',
  },
  {
    title: 'Burundi',
    value: 'BI',
  },
  {
    title: 'Cambodia',
    value: 'KH',
  },
  {
    title: 'Cameroon',
    value: 'CM',
  },
  {
    title: 'Canada',
    value: 'CA',
  },
  {
    title: 'Cape Verde',
    value: 'CV',
  },
  {
    title: 'Cayman Islands',
    value: 'KY',
  },
  {
    title: 'Central African Republic',
    value: 'CF',
  },
  {
    title: 'Chad',
    value: 'TD',
  },
  {
    title: 'Chile',
    value: 'CL',
  },
  {
    title: 'China',
    value: 'CN',
  },
  {
    title: 'Christmas Island',
    value: 'CX',
  },
  {
    title: 'Cocos Islands',
    value: 'CC',
  },
  {
    title: 'Columbia',
    value: 'CO',
  },
  {
    title: 'Comoros',
    value: 'KM',
  },
  {
    title: 'Cook Islands',
    value: 'CK',
  },
  {
    title: 'Costa Rica',
    value: 'CR',
  },
  {
    title: 'Croatia',
    value: 'HR',
  },
  {
    title: 'Cuba',
    value: 'CU',
  },
  {
    title: 'Curacao',
    value: 'CW',
  },
  {
    title: 'Cyprus',
    value: 'CY',
  },
  {
    title: 'Czech Republic',
    value: 'CZ',
  },
  {
    title: 'Democratic Republic of the Congo',
    value: 'CD',
  },
  {
    title: 'Denmark',
    value: 'DK',
  },
  {
    title: 'Djibouti',
    value: 'DJ',
  },
  {
    title: 'Dominica',
    value: 'DM',
  },
  {
    title: 'Dominican Republic',
    value: 'DO',
  },
  {
    title: 'East Timor',
    value: 'TL',
  },
  {
    title: 'Ecuador',
    value: 'EC',
  },
  {
    title: 'Egypt',
    value: 'EG',
  },
  {
    title: 'El Salvador',
    value: 'SV',
  },
  {
    title: 'Equatorial Guinea',
    value: 'GQ',
  },
  {
    title: 'Eritrea',
    value: 'ER',
  },
  {
    title: 'Estonia',
    value: 'EE',
  },
  {
    title: 'Ethiopia',
    value: 'ET',
  },
  {
    title: 'Falkland Islands',
    value: 'FK',
  },
  {
    title: 'Faroe Islands',
    value: 'FO',
  },
  {
    title: 'Fiji',
    value: 'FJ',
  },
  {
    title: 'Finland',
    value: 'FI',
  },
  {
    title: 'France',
    value: 'FR',
  },
  {
    title: 'French Guiana',
    value: 'GF',
  },
  {
    title: 'French Polynesia',
    value: 'PF',
  },
  {
    title: 'French Southern Territories',
    value: 'TF',
  },
  {
    title: 'Gabon',
    value: 'GA',
  },
  {
    title: 'Gambia',
    value: 'GM',
  },
  {
    title: 'Georgia',
    value: 'GE',
  },
  {
    title: 'Germany',
    value: 'DE',
  },
  {
    title: 'Ghana',
    value: 'GH',
  },
  {
    title: 'Gibraltar',
    value: 'GI',
  },
  {
    title: 'Greece',
    value: 'GR',
  },
  {
    title: 'Greenland',
    value: 'GL',
  },
  {
    title: 'Grenada',
    value: 'GD',
  },
  {
    title: 'Guadeloupe',
    value: 'GP',
  },
  {
    title: 'Guam',
    value: 'GU',
  },
  {
    title: 'Guatemala',
    value: 'GT',
  },
  {
    title: 'Guernsey',
    value: 'GG',
  },
  {
    title: 'Guinea',
    value: 'GN',
  },
  {
    title: 'Guinea-Bissau',
    value: 'GW',
  },
  {
    title: 'Guyana',
    value: 'GY',
  },
  {
    title: 'Haiti',
    value: 'HT',
  },
  {
    title: 'Heard Island and McDonald Islands',
    value: 'HM',
  },
  {
    title: 'Honduras',
    value: 'HN',
  },
  {
    title: 'Hong Kong',
    value: 'HK',
  },
  {
    title: 'Hungary',
    value: 'HU',
  },
  {
    title: 'Iceland',
    value: 'IS',
  },
  {
    title: 'India',
    value: 'IN',
  },
  {
    title: 'Indonesia',
    value: 'ID',
  },
  {
    title: 'Iran',
    value: 'IR',
  },
  {
    title: 'Iraq',
    value: 'IQ',
  },
  {
    title: 'Ireland',
    value: 'IE',
  },
  {
    title: 'Isle of Man',
    value: 'IM',
  },
  {
    title: 'Israel',
    value: 'IL',
  },
  {
    title: 'Italy',
    value: 'IT',
  },
  {
    title: 'Ivory Coast',
    value: 'CI',
  },
  {
    title: 'Jamaica',
    value: 'JM',
  },
  {
    title: 'Japan',
    value: 'JP',
  },
  {
    title: 'Jersey',
    value: 'JE',
  },
  {
    title: 'Jordan',
    value: 'JO',
  },
  {
    title: 'Kazakhstan',
    value: 'KZ',
  },
  {
    title: 'Kenya',
    value: 'KE',
  },
  {
    title: 'Kiribati',
    value: 'KI',
  },
  {
    title: 'Kosovo',
    value: 'XK',
  },
  {
    title: 'Kuwait',
    value: 'KW',
  },
  {
    title: 'Kyrgyzstan',
    value: 'KG',
  },
  {
    title: 'Laos',
    value: 'LA',
  },
  {
    title: 'Latvia',
    value: 'LV',
  },
  {
    title: 'Lebanon',
    value: 'LB',
  },
  {
    title: 'Lesotho',
    value: 'LS',
  },
  {
    title: 'Liberia',
    value: 'LR',
  },
  {
    title: 'Libya',
    value: 'LY',
  },
  {
    title: 'Liechtenstein',
    value: 'LI',
  },
  {
    title: 'Lithuania',
    value: 'LT',
  },
  {
    title: 'Luxembourg',
    value: 'LU',
  },
  {
    title: 'Macau',
    value: 'MO',
  },
  {
    title: 'Macedonia',
    value: 'MK',
  },
  {
    title: 'Madagascar',
    value: 'MG',
  },
  {
    title: 'Malawi',
    value: 'MW',
  },
  {
    title: 'Malaysia',
    value: 'MY',
  },
  {
    title: 'Maldives',
    value: 'MV',
  },
  {
    title: 'Mali',
    value: 'ML',
  },
  {
    title: 'Malta',
    value: 'MT',
  },
  {
    title: 'Marshall Islands',
    value: 'MH',
  },
  {
    title: 'Martinique',
    value: 'MQ',
  },
  {
    title: 'Mauritania',
    value: 'MR',
  },
  {
    title: 'Mauritius',
    value: 'MU',
  },
  {
    title: 'Mayotte',
    value: 'YT',
  },
  {
    title: 'Mexico',
    value: 'MX',
  },
  {
    title: 'Micronesia',
    value: 'FM',
  },
  {
    title: 'Moldova',
    value: 'MD',
  },
  {
    title: 'Monaco',
    value: 'MC',
  },
  {
    title: 'Mongolia',
    value: 'MN',
  },
  {
    title: 'Montenegro',
    value: 'ME',
  },
  {
    title: 'Montserrat',
    value: 'MS',
  },
  {
    title: 'Morocco',
    value: 'MA',
  },
  {
    title: 'Mozambique',
    value: 'MZ',
  },
  {
    title: 'Myanmar',
    value: 'MM',
  },
  {
    title: 'Namibia',
    value: 'NA',
  },
  {
    title: 'Nauru',
    value: 'NR',
  },
  {
    title: 'Nepal',
    value: 'NP',
  },
  {
    title: 'Netherlands',
    value: 'NL',
  },
  {
    title: 'Netherlands Antilles',
    value: 'AN',
  },
  {
    title: 'New Caledonia',
    value: 'NC',
  },
  {
    title: 'New Zealand',
    value: 'NZ',
  },
  {
    title: 'Nicaragua',
    value: 'NI',
  },
  {
    title: 'Niger',
    value: 'NE',
  },
  {
    title: 'Nigeria',
    value: 'NG',
  },
  {
    title: 'Niue',
    value: 'NU',
  },
  {
    title: 'Norfolk Island',
    value: 'NF',
  },
  {
    title: 'North Korea',
    value: 'KP',
  },
  {
    title: 'Northern Mariana Islands',
    value: 'MP',
  },
  {
    title: 'Norway',
    value: 'NO',
  },
  {
    title: 'Oman',
    value: 'OM',
  },
  {
    title: 'Pakistan',
    value: 'PK',
  },
  {
    title: 'Palau',
    value: 'PW',
  },
  {
    title: 'Palestine',
    value: 'PS',
  },
  {
    title: 'Panama',
    value: 'PA',
  },
  {
    title: 'Papua New Guinea',
    value: 'PG',
  },
  {
    title: 'Paraguay',
    value: 'PY',
  },
  {
    title: 'Peru',
    value: 'PE',
  },
  {
    title: 'Philippines',
    value: 'PH',
  },
  {
    title: 'Pitcairn Islands',
    value: 'PN',
  },
  {
    title: 'Poland',
    value: 'PL',
  },
  {
    title: 'Portugal',
    value: 'PT',
  },
  {
    title: 'Puerto Rico',
    value: 'PR',
  },
  {
    title: 'Qatar',
    value: 'QA',
  },
  {
    title: 'Republic of the Congo',
    value: 'CG',
  },
  {
    title: 'Reunion',
    value: 'RE',
  },
  {
    title: 'Romania',
    value: 'RO',
  },
  {
    title: 'Russia',
    value: 'RU',
  },
  {
    title: 'Rwanda',
    value: 'RW',
  },
  {
    title: 'Saint Helena',
    value: 'SH',
  },
  {
    title: 'Saint Kitts and Nevis',
    value: 'KN',
  },
  {
    title: 'Saint Lucia',
    value: 'LC',
  },
  {
    title: 'Saint Martin',
    value: 'MF',
  },
  {
    title: 'Saint Pierre and Miquelon',
    value: 'PM',
  },
  {
    title: 'Saint Vincent and the Grenadines',
    value: 'VC',
  },
  {
    title: 'Samoa',
    value: 'WS',
  },
  {
    title: 'San Marino',
    value: 'SM',
  },
  {
    title: 'Sao Tome and Principe',
    value: 'ST',
  },
  {
    title: 'Saudi Arabia',
    value: 'SA',
  },
  {
    title: 'Senegal',
    value: 'SN',
  },
  {
    title: 'Serbia',
    value: 'RS',
  },
  {
    title: 'Seychelles',
    value: 'SC',
  },
  {
    title: 'Sierra Leone',
    value: 'SL',
  },
  {
    title: 'Singapore',
    value: 'SG',
  },
  {
    title: 'Sint Maarten',
    value: 'SX',
  },
  {
    title: 'Slovakia',
    value: 'SK',
  },
  {
    title: 'Slovenia',
    value: 'SI',
  },
  {
    title: 'Solomon Islands',
    value: 'SB',
  },
  {
    title: 'Somalia',
    value: 'SO',
  },
  {
    title: 'South Africa',
    value: 'ZA',
  },
  {
    title: 'South Georgia and the South Sandwich Islands',
    value: 'GS',
  },
  {
    title: 'South Korea',
    value: 'KR',
  },
  {
    title: 'South Sudan',
    value: 'SS',
  },
  {
    title: 'Spain',
    value: 'ES',
  },
  {
    title: 'Sri Lanka',
    value: 'LK',
  },
  {
    title: 'Sudan',
    value: 'SD',
  },
  {
    title: 'Suriname',
    value: 'SR',
  },
  {
    title: 'Svalbard and Jan Mayen',
    value: 'SJ',
  },
  {
    title: 'Swaziland',
    value: 'SZ',
  },
  {
    title: 'Sweden',
    value: 'SE',
  },
  {
    title: 'Switzerland',
    value: 'CH',
  },
  {
    title: 'Syria',
    value: 'SY',
  },
  {
    title: 'Taiwan',
    value: 'TW',
  },
  {
    title: 'Tajikistan',
    value: 'TJ',
  },
  {
    title: 'Tanzania',
    value: 'TZ',
  },
  {
    title: 'Thailand',
    value: 'TH',
  },
  {
    title: 'Togo',
    value: 'TG',
  },
  {
    title: 'Tokelau',
    value: 'TK',
  },
  {
    title: 'Tonga',
    value: 'TO',
  },
  {
    title: 'Trinidad and Tobago',
    value: 'TT',
  },
  {
    title: 'Tunisia',
    value: 'TN',
  },
  {
    title: 'Turkey',
    value: 'TR',
  },
  {
    title: 'Turkmenistan',
    value: 'TM',
  },
  {
    title: 'Turks and Caicos Islands',
    value: 'TC',
  },
  {
    title: 'Tuvalu',
    value: 'TV',
  },
  {
    title: 'U.S. Minor Outlying Islands',
    value: 'UM',
  },
  {
    title: 'U.S. Virgin Islands',
    value: 'VI',
  },
  {
    title: 'Uganda',
    value: 'UG',
  },
  {
    title: 'Ukraine',
    value: 'UA',
  },
  {
    title: 'United Arab Emirates',
    value: 'AE',
  },
  {
    title: 'United Kingdom',
    value: 'GB',
  },
  {
    title: 'United States',
    value: 'US',
  },
  {
    title: 'Uruguay',
    value: 'UY',
  },
  {
    title: 'Uzbekistan',
    value: 'UZ',
  },
  {
    title: 'Vanuatu',
    value: 'VU',
  },
  {
    title: 'Vatican City',
    value: 'VA',
  },
  {
    title: 'Venezuela',
    value: 'VE',
  },
  {
    title: 'Vietnam',
    value: 'VN',
  },
  {
    title: 'Wallis and Futuna',
    value: 'WF',
  },
  {
    title: 'Western Sahara',
    value: 'EH',
  },
  {
    title: 'Yemen',
    value: 'YE',
  },
  {
    title: 'Zambia',
    value: 'ZM',
  },
  {
    title: 'Zimbabwe',
    value: 'ZW',
  },
] as const;

/**
 * Resolve a human-readable country name from an ISO country code using
 * {@link COUNTRIES}. If no matching country is found, the original value is
 * returned as-is (it may already be a country name).
 * TODO: Ask BE for fix so we can make BE send correct data (country name
 * instead of country code) and remove this mapping.
 */
export const getCountryNameFromCode = (
  countryCode?: string | null,
): string | null | undefined => {
  if (!countryCode) {
    return countryCode;
  }
  const match = COUNTRIES.find(
    (country) => country.value === countryCode.toUpperCase(),
  );
  return match?.title ?? countryCode;
};

export const INDUSTRIES = [
  'Abrasives and Nonmetallic Minerals Manufacturing',
  'Accessible Architecture and Design',
  'Accessible Hardware Manufacturing',
  'Accommodation and Food Services',
  'Accounting',
  'Administration of Justice',
  'Administrative and Support Services',
  'Advertising Services',
  'Agricultural Chemical Manufacturing',
  'Agriculture, Construction, Mining Machinery Manufacturing',
  'Air, Water, and Waste Program Management',
  'Airlines and Aviation',
  'Alternative Dispute Resolution',
  'Alternative Fuel Vehicle Manufacturing',
  'Alternative Medicine',
  'Ambulance Services',
  'Amusement Parks and Arcades',
  'Animal Feed Manufacturing',
  'Animation',
  'Animation and Post-production',
  'Animation at Post-production',
  'Apparel & Fashion',
  'Apparel Manufacturing',
  'Appliances, Electrical, and Electronics Manufacturing',
  'Architectural and Structural Metal Manufacturing',
  'Architecture and Planning',
  'Armed Forces',
  'Artificial Rubber and Synthetic Fiber Manufacturing',
  'Artists and Writers',
  'Arts & Crafts',
  'Arts and Crafts',
  'Audio and Video Equipment Manufacturing',
  'Automation Machinery Manufacturing',
  'Automotive',
  'Aviation & Aerospace',
  'Aviation and Aerospace Component Manufacturing',
  'Baked Goods Manufacturing',
  'Banking',
  'Bars, Taverns, and Nightclubs',
  'Bed-and-Breakfast, Hostel, Homestay',
  'Bed-and-Breakfasts, Hostels, Homestays',
  'Beverage Manufacturing',
  'Biomass Electric Power Generation',
  'Biotechnology',
  'Biotechnology Research',
  'Blockchain Services',
  'Blogs',
  'Boilers, Tanks, and Shipping Container Manufacturing',
  'Book Publishing',
  'Book and Periodical Publishing',
  'Breweries',
  'Broadcast Media Production and Distribution',
  'Building Construction',
  'Building Equipment Contractors',
  'Building Finishing Contractors',
  'Building Materials',
  'Building Structure and Exterior Contractors',
  'Business Consulting and Services',
  'Business Content',
  'Business Intelligence Platforms',
  'Business Supplies & Equipment',
  'Capital Markets',
  'Caterers',
  'Chemical Manufacturing',
  'Chemical Raw Materials Manufacturing',
  'Child Day Care Services',
  'Chiropractors',
  'Civic and Social Organizations',
  'Civil Engineering',
  'Claims Adjusting, Actuarial Services',
  'Clay and Refractory Products Manufacturing',
  'Climate Data and Analytics',
  'Climate Technology Product Manufacturing',
  'Coal Mining',
  'Collection Agencies',
  'Commercial Real Estate',
  'Commercial and Industrial Equipment Rental',
  'Commercial and Industrial Machinery Maintenance',
  'Commercial and Service Industry Machinery Manufacturing',
  'Communications Equipment Manufacturing',
  'Community Development and Urban Planning',
  'Community Services',
  'Computer Games',
  'Computer Hardware',
  'Computer Hardware Manufacturing',
  'Computer Networking',
  'Computer Networking Products',
  'Computer and Network Security',
  'Computers and Electronics Manufacturing',
  'Conservation Programs',
  'Construction',
  'Construction Hardware Manufacturing',
  'Consumer Electronics',
  'Consumer Goods',
  'Consumer Goods Rental',
  'Consumer Services',
  'Correctional Institutions',
  'Cosmetics',
  'Cosmetology and Barber Schools',
  'Courts of Law',
  'Credit Intermediation',
  'Custom Software Development ng IT System',
  'Cutlery and Handtool Manufacturing',
  'Dairy',
  'Dairy Product Manufacturing',
  'Dance Companies',
  'Data Infrastructure and Analytics',
  'Data Security Software Products',
  'Defense & Space',
  'Defense and Space Manufacturing',
  'Dentists',
  'Design Services',
  'Desktop Computing Software Products',
  'Digital Accessibility Services',
  'Distilleries',
  'E-Learning',
  'E-Learning Providers',
  'E-learning',
  'Economic Programs',
  'Education',
  'Education Administration Programs',
  'Education Management',
  'Electric Lighting Equipment Manufacturing',
  'Electric Power Generation',
  'Electric Power Transmission, Control, and Distribution',
  'Electrical Equipment Manufacturing',
  'Electronic and Precision Equipment Maintenance',
  'Embedded Software Products',
  'Emergency and Relief Services',
  'Engineering Services',
  'Engines and Power Transmission Equipment Manufacturing',
  'Entertainment Providers',
  'Environmental Quality Programs',
  'Environmental Services',
  'Equipment Rental Services',
  'Events Services',
  'Executive Offices',
  'Executive Search Services',
  'Fabricated Metal Products',
  'Facilities Services',
  'Family Planning Centers',
  'Farming',
  'Farming, Ranching, Forestry',
  'Fashion Accessories Manufacturing',
  'Financial Services',
  'Fine Art',
  'Fine Arts Schools',
  'Fire Protection',
  'Fisheries',
  'Flight Training',
  'Food & Beverages',
  'Food and Beverage Manufacturing',
  'Food and Beverage Retail',
  'Food and Beverage Services',
  'Footwear Manufacturing',
  'Footwear and Leather Goods Repair',
  'Forestry and Logging',
  'Fossil Fuel Electric Power Generation',
  'Freight and Package Transportation',
  'Fruit and Vegetable Preserves Manufacturing',
  'Fuel Cell Manufacturing',
  'Fundraising',
  'Furniture',
  'Furniture and Home Furnishings Manufacturing',
  'Gambling Facilities and Casinos',
  'Geothermal Electric Power Generation',
  'Glass Product Manufacturing',
  'Glass, Ceramics and Concrete Manufacturing',
  'Golf Courses and Country Clubs',
  'Government Administration',
  'Government Relations',
  'Government Relations Services',
  'Graphic Design',
  'Ground Passenger Transportation',
  'HVAC and Refrigeration Equipment Manufacturing',
  'Headhunting',
  'Health and Human Services',
  'Health, Wellness & Fitness',
  'Health, Wellness and Fitness',
  'Higher Education',
  'Highway, Street, and Bridge Construction',
  'Historical Sites',
  'Holding Companies',
  'Home Health Care Services',
  'Horticulture',
  'Hospitality',
  'Hospitals',
  'Hospitals and Health Care',
  'Hotels and Motels',
  'Household Appliance Manufacturing',
  'Household Services',
  'Household and Institutional Furniture Manufacturing',
  'Housing Programs',
  'Housing and Community Development',
  'Human Resources',
  'Human Resources Services',
  'Human resources',
  'Hydroelectric Power Generation',
  'IT Services and IT Consulting',
  'IT System Custom Software Development',
  'IT System Data Services',
  'IT System Design Services',
  'IT System Installation and Disposal',
  'IT System Operations and Maintenance',
  'IT System Testing and Evaluation',
  'IT System Training and Support',
  'Import & Export',
  'Import at Export',
  'Individual and Family Services',
  'Industrial Automation',
  'Industrial Machinery Manufacturing',
  'Industry Associations',
  'Information Services',
  'Information Technology & Services',
  'Information Technology and Services',
  'Insurance',
  'Insurance Agencies and Brokerages',
  'Insurance Carriers',
  'Insurance and Employee Benefit Funds',
  'Interior Design',
  'Interior design',
  'International Affairs',
  'International Trade and Development',
  'International Trade at Development',
  'Internet Marketplace Platforms',
  'Internet News',
  'Internet Publishing',
  'Interurban and Rural Bus Services',
  'Investment Advice',
  'Investment Banking',
  'Investment Management',
  'Janitorial Services',
  'Landscaping Services',
  'Language Schools',
  'Laundry and Drycleaning Services',
  'Law Enforcement',
  'Law Practice',
  'Leasing Non-residential Real Estate',
  'Leasing Real Estat Nonhunian',
  'Leasing Residential Real Estate',
  'Leasing ng Non-residential Real Estate',
  'Leather Product Manufacturing',
  'Legal Services',
  'Legislative Offices',
  'Leisure, Travel & Tourism',
  'Libraries',
  'Lime and Gypsum Products Manufacturing',
  'Loan Brokers',
  'Luxury Goods & Jewelry',
  'Machinery Manufacturing',
  'Magnetic and Optical Media Manufacturing',
  'Manufacturing',
  'Maritime',
  'Maritime Transportation',
  'Market Research',
  'Marketing Services',
  'Mattress and Blinds Manufacturing',
  'Measuring and Control Instrument Manufacturing',
  'Mechanical Or Industrial Engineering',
  'Mechanical or Industrial Engineering',
  'Media Production',
  'Media and Telecommunications',
  'Medical Device',
  'Medical Devices',
  'Medical Equipment Manufacturing',
  'Medical Practices',
  'Medical and Diagnostic Laboratories',
  'Mental Health Care',
  'Metal Ore Mining',
  'Metal Treatments',
  'Metal Valve, Ball, and Roller Manufacturing',
  'Metalworking Machinery Manufacturing',
  'Mga Bed-and-Breakfast, Hostel, Homestay',
  'Mga Entertainment Provider',
  'Mga Internet Marketplace Platform',
  'Mga Philanthropic Fundraising Service',
  'Military and International Affairs',
  'Mobile Computing Software Products',
  'Mobile Food Services',
  'Mobile Gaming Apps',
  'Motor Vehicle Manufacturing',
  'Motor Vehicle Parts Manufacturing',
  'Movies and Sound Recording',
  'Movies, Videos, and Sound',
  'Museums',
  'Museums, Historical Sites, and Zoos',
  'Music',
  'Musicians',
  'Nanotechnology Research',
  'Natural Gas Distribution',
  'Natural Gas Extraction',
  'Newspaper Publishing',
  'Non-profit Organization Management',
  'Non-profit Organizations',
  'Nonmetallic Mineral Mining',
  'Nonprofit Organization Management',
  'Nonresidential Building Construction',
  'Nuclear Electric Power Generation',
  'Nursing Homes and Residential Care Facilities',
  'Office Administration',
  'Office Furniture and Fixtures Manufacturing',
  'Oil Extraction',
  'Oil and Coal Product Manufacturing',
  'Oil and Gas',
  'Oil, Gas, and Mining',
  'Online Audio and Video Media',
  'Online Audio at Video Media',
  'Online Media',
  'Online and Mail Order Retail',
  'Operations Consulting',
  'Operations management consulting',
  'Optometrists',
  'Outpatient Care Centers',
  'Outsourcing & Offshoring',
  'Outsourcing and Offshoring Consulting',
  'Outsourcing/Offshoring',
  'Packaging & Containers',
  'Packaging and Containers Manufacturing',
  'Paint, Coating, and Adhesive Manufacturing',
  'Paper & Forest Products',
  'Paper and Forest Product Manufacturing',
  'Pension Funds',
  'Performing Arts and Spectator Sports',
  'Periodical Publishing',
  'Personal Care Product Manufacturing',
  'Personal Care Services',
  'Personal and Laundry Services',
  'Pet Services',
  'Pharmaceutical Manufacturing',
  'Philanthropic Fundraising Services',
  'Philanthropy',
  'Photography',
  'Physical, Occupational and Speech Therapists',
  'Physicians',
  'Pipeline Transportation',
  'Plastics Manufacturing',
  'Plastics and Rubber Product Manufacturing',
  'Political Organizations',
  'Postal Services',
  'Primary Metal Manufacturing',
  'Primary and Secondary Education',
  'Printing Services',
  'Professional Organizations',
  'Professional Services',
  'Professional Training and Coaching',
  'Program Development',
  'Public Assistance Programs',
  'Public Health',
  'Public Policy',
  'Public Policy Offices',
  'Public Relations and Communications Services',
  'Public Safety',
  'Racetracks',
  'Radio and Television Broadcasting',
  'Rail Transportation',
  'Railroad Equipment Manufacturing',
  'Ranching',
  'Ranching and Fisheries',
  'Real Estate',
  'Real Estate Agents and Brokers',
  'Real Estate and Equipment Rental Services',
  'Recreational Facilities',
  'Regenerative Design',
  'Religious Institutions',
  'Renewable Energy Equipment Manufacturing',
  'Renewable Energy Power Generation',
  'Renewable Energy Semiconductor Manufacturing',
  'Renewables & Environment',
  'Repair and Maintenance',
  'Research',
  'Research Services',
  'Residential Building Construction',
  'Restaurants',
  'Retail',
  'Retail Apparel and Fashion',
  'Retail Appliances, Electrical, and Electronic Equipment',
  'Retail Art Dealers',
  'Retail Art Supplies',
  'Retail Books and Printed News',
  'Retail Building Materials and Garden Equipment',
  'Retail Florists',
  'Retail Furniture and Home Furnishings',
  'Retail Gasoline',
  'Retail Groceries',
  'Retail Health and Personal Care Products',
  'Retail Luxury Goods and Jewelry',
  'Retail Motor Vehicles',
  'Retail Musical Instruments',
  'Retail Office Equipment',
  'Retail Office Supplies and Gifts',
  'Retail Pharmacies',
  'Retail Recyclable Materials & Used Merchandise',
  'Retail ng Online at Mail Order',
  'Reupholstery and Furniture Repair',
  'Robot Manufacturing',
  'Robotics Engineering',
  'Robotics engineering',
  'Rubber Products Manufacturing',
  'Satellite Telecommunications',
  'School and Employee Bus Services',
  'Seafood Product Manufacturing',
  'Secretarial Schools',
  'Securities and Commodity Exchanges',
  'Security Guards and Patrol Services',
  'Security Systems Services',
  'Security and Investigations',
  'Semiconductor Manufacturing',
  'Semiconductors',
  'Services for Renewable Energy',
  'Services for the Elderly and Disabled',
  'Sheet Music Publishing',
  'Shipbuilding',
  'Shuttles and Special Needs Transportation Services',
  'Sightseeing Transportation',
  'Skiing Facilities',
  'Smart Meter Manufacturing',
  'Soap and Cleaning Product Manufacturing',
  'Social Networking Platforms',
  'Software Development',
  'Solar Electric Power Generation',
  'Sound Recording',
  'Space Research and Technology',
  'Specialty Trade Contractors',
  'Spectator Sports',
  'Sporting Goods',
  'Sporting Goods Manufacturing',
  'Sports Teams and Clubs',
  'Sports and Recreation Instruction',
  'Spring and Wire Product Manufacturing',
  'Staffing and Recruiting',
  'Staffing at Recruiting',
  'Steam and Air-Conditioning Supply',
  'Strategic Management Services',
  'Subdivision of Land',
  'Sugar and Confectionery Product Manufacturing',
  'Surveying and Mapping Services',
  'Taxi and Limousine Services',
  'Technical and Vocational Training',
  'Technology, Information and Internet',
  'Technology, Information and Media',
  'Telecommunications',
  'Telecommunications Carriers',
  'Telephone Call Centers',
  'Temporary Help Services',
  'Textile Manufacturing',
  'Theater Companies',
  'Think Tanks',
  'Think tank',
  'Think tanks',
  'Tobacco',
  'Tobacco Manufacturing',
  'Translation and Localization',
  'Transportation Equipment Manufacturing',
  'Transportation Programs',
  'Transportation, Logistics, Supply Chain and Storage',
  'Transportation/Trucking/Railroad',
  'Travel Arrangements',
  'Truck Transportation',
  'Trusts and Estates',
  'Turned Products and Fastener Manufacturing',
  'Urban Transit Services',
  'Utilities',
  'Utilities Administration',
  'Utility System Construction',
  'Vehicle Repair and Maintenance',
  'Venture Capital and Private Equity Principals',
  'Veterinary',
  'Veterinary Services',
  'Vocational Rehabilitation Services',
  'Warehousing',
  'Warehousing and Storage',
  'Warehousing at Storage',
  'Waste Collection',
  'Waste Treatment and Disposal',
  'Water Supply and Irrigation Systems',
  'Water, Waste, Steam, and Air Conditioning Services',
  'Wellness and Fitness Services',
  'Wholesale',
  'Wholesale Alcoholic Beverages',
  'Wholesale Apparel and Sewing Supplies',
  'Wholesale Appliances, Electrical, and Electronics',
  'Wholesale Building Materials',
  'Wholesale Chemical and Allied Products',
  'Wholesale Computer Equipment',
  'Wholesale Drugs and Sundries',
  'Wholesale Food and Beverage',
  'Wholesale Footwear',
  'Wholesale Furniture and Home Furnishings',
  'Wholesale Hardware, Plumbing, Heating Equipment',
  'Wholesale Import and Export',
  'Wholesale Luxury Goods and Jewelry',
  'Wholesale Machinery',
  'Wholesale Metals and Minerals',
  'Wholesale Motor Vehicles and Parts',
  'Wholesale Paper Products',
  'Wholesale Petroleum and Petroleum Products',
  'Wholesale Photography Equipment and Supplies',
  'Wholesale Raw Farm Products',
  'Wholesale Recyclable Materials',
  'Wholesale ng Machinery',
  'Wind Electric Power Generation',
  'Wine & Spirits',
  'Wine at Spirits',
  'Wineries',
  'Wireless Services',
  "Women's Handbag Manufacturing",
  'Wood Product Manufacturing',
  'Writing and Editing',
  'Zoos and Botanical Gardens',
] as const;

export type Industry = (typeof INDUSTRIES)[number];
